import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import Box from '@mui/material/Box';

import { isToday } from 'bento-ordering/base/utils/dates';
import {
  fulfillmentOptions,
  ALL_ONLINE_ORDERING_FULFILLMENT_METHODS
} from 'constants/fulfillmentOptions';
import {
  MILITARY_TIME,
  DATE_INPUT_FORMAT,
  FULL_DAY_OUTPUT_FORMAT,
  HOUR_MINUTE_DAY_TIME
} from 'bento-ordering/base/constants/timeFormat';

import AlertSnackbar from 'bento-ordering/base/components/Snackbar/AlertSnackbar';
import {
  SnackbarMessage,
  SnackbarTitle
} from 'bento-ordering/base/components/Snackbar/SnackbarTypography';
import { isMobileMedia } from 'bento-ordering/utils/media';
import {
  ITEM_DESCRIPTION_AND_PRICES,
  MENU_ITEM_NAMES
} from 'styles/constants/appearanceSectionNames';

const NoAsapForPickupAndDeliveryMessage = ({
  pickupTime,
  pickupWeekday,
  deliveryTime,
  deliveryWeekday,
  isPickupToday,
  isDeliveryToday,
  snackbarHeadingOverride
}) => {
  const titleStyles = { variant: MENU_ITEM_NAMES };
  const pickupMessageStyles = { variant: ITEM_DESCRIPTION_AND_PRICES };
  const deliveryMessageStyles = { variant: ITEM_DESCRIPTION_AND_PRICES };
  return (
    <span>
      <Box pb={1}>
        <SnackbarTitle component="p" {...titleStyles}>
          {snackbarHeadingOverride || 'No ASAP Pickup or Delivery'}
        </SnackbarTitle>
      </Box>
      <SnackbarMessage component="p" {...pickupMessageStyles}>
        {isPickupToday
          ? `Our next pickup time is later today at ${pickupTime}.`
          : `Our next pickup time is ${pickupWeekday} at ${pickupTime}.`}
      </SnackbarMessage>
      <SnackbarMessage component="p" {...deliveryMessageStyles}>
        {isDeliveryToday
          ? `Delivery is next available later today at ${deliveryTime}.`
          : `Delivery is next available ${deliveryWeekday} at ${deliveryTime}.`}
      </SnackbarMessage>
    </span>
  );
};

NoAsapForPickupAndDeliveryMessage.propTypes = {
  pickupTime: PropTypes.string,
  pickupWeekday: PropTypes.string,
  deliveryTime: PropTypes.string,
  deliveryWeekday: PropTypes.string,
  snackbarHeadingOverride: PropTypes.string,
  isPickupToday: PropTypes.bool,
  isDeliveryToday: PropTypes.bool
};

const NoAsapForPickupMessage = ({
  pickupTime,
  pickupWeekday,
  isPickupToday,
  snackbarHeadingOverride
}) => {
  const titleStyles = { variant: MENU_ITEM_NAMES };
  const messageStyles = { variant: ITEM_DESCRIPTION_AND_PRICES };

  return (
    <span>
      <Box pb={1}>
        <SnackbarTitle {...titleStyles}>
          <strong>{snackbarHeadingOverride || 'No ASAP Pickup'}</strong>
        </SnackbarTitle>
      </Box>
      <SnackbarMessage {...messageStyles}>
        {isPickupToday
          ? `Our next pickup time is later today at ${pickupTime}.`
          : `Our next pickup time is ${pickupWeekday} at ${pickupTime}.`}
      </SnackbarMessage>
    </span>
  );
};

NoAsapForPickupMessage.propTypes = {
  pickupTime: PropTypes.string,
  pickupWeekday: PropTypes.string,
  isPickupToday: PropTypes.bool,
  snackbarHeadingOverride: PropTypes.string
};

const NoAsapForDeliveryMessage = ({
  deliveryWeekday,
  deliveryTime,
  isDeliveryToday,
  snackbarHeadingOverride
}) => {
  const titleStyles = { variant: MENU_ITEM_NAMES };
  const messageStyles = { variant: ITEM_DESCRIPTION_AND_PRICES };
  return (
    <span>
      <Box pb={1}>
        <SnackbarTitle {...titleStyles}>
          <strong>{snackbarHeadingOverride || 'No ASAP Delivery'}</strong>
        </SnackbarTitle>
      </Box>
      <SnackbarMessage {...messageStyles}>
        {isDeliveryToday
          ? `Delivery is next available later today at ${deliveryTime}.`
          : `Delivery is next available ${deliveryWeekday} at ${deliveryTime}.`}
      </SnackbarMessage>
    </span>
  );
};

NoAsapForDeliveryMessage.propTypes = {
  deliveryTime: PropTypes.string,
  deliveryWeekday: PropTypes.string,
  isDeliveryToday: PropTypes.bool,
  snackbarHeadingOverride: PropTypes.string
};

const NoAsapSnackbar = ({
  open,
  onClose,
  pickupTime,
  pickupDate,
  deliveryDate,
  deliveryTime,
  isAsapAvailableForPickup,
  isAsapAvailableForDelivery,
  today,
  cartWorkingCopy,
  availableFulfillmentOptions,
  snackbarHeadingOverride
}) => {
  const isPickupToday = isToday(pickupDate, today);
  const isDeliveryToday = isToday(deliveryDate, today);
  const formattedPickupTime = moment(pickupTime, MILITARY_TIME).format(
    HOUR_MINUTE_DAY_TIME
  );
  const formattedPickupWeekday = moment(pickupDate, DATE_INPUT_FORMAT).format(
    FULL_DAY_OUTPUT_FORMAT
  );
  const formattedDeliveryTime = moment(deliveryTime, MILITARY_TIME).format(
    HOUR_MINUTE_DAY_TIME
  );
  const formattedDeliveryWeekday = moment(
    deliveryDate,
    DATE_INPUT_FORMAT
  ).format(FULL_DAY_OUTPUT_FORMAT);
  const getMessage = () => {
    if (
      !isAsapAvailableForPickup &&
      !isAsapAvailableForDelivery &&
      availableFulfillmentOptions.length === 2
    ) {
      return (
        <NoAsapForPickupAndDeliveryMessage
          pickupTime={formattedPickupTime}
          pickupWeekday={formattedPickupWeekday}
          deliveryTime={formattedDeliveryTime}
          deliveryWeekday={formattedDeliveryWeekday}
          isPickupToday={isPickupToday}
          isDeliveryToday={isDeliveryToday}
          snackbarHeadingOverride={snackbarHeadingOverride}
        />
      );
    } else if (
      !isAsapAvailableForPickup &&
      cartWorkingCopy.online_ordering_fulfillment_method ===
        fulfillmentOptions.PICKUP &&
      availableFulfillmentOptions.includes('pickup')
    ) {
      return (
        <NoAsapForPickupMessage
          pickupTime={formattedPickupTime}
          pickupWeekday={formattedPickupWeekday}
          isPickupToday={isPickupToday}
          snackbarHeadingOverride={snackbarHeadingOverride}
        />
      );
    } else if (
      !isAsapAvailableForDelivery &&
      cartWorkingCopy.online_ordering_fulfillment_method ===
        fulfillmentOptions.DELIVERY &&
      availableFulfillmentOptions.includes('delivery')
    ) {
      return (
        <NoAsapForDeliveryMessage
          deliveryTime={formattedDeliveryTime}
          deliveryWeekday={formattedDeliveryWeekday}
          isDeliveryToday={isDeliveryToday}
          snackbarHeadingOverride={snackbarHeadingOverride}
        />
      );
    }
    return null;
  };
  const message = getMessage();

  if (!message) {
    return null;
  }

  return (
    <AlertSnackbar
      onClose={onClose}
      open={open}
      message={message}
      cartWorkingCopy={cartWorkingCopy}
      vertical={isMobileMedia() ? 'bottom' : 'top'}
    />
  );
};

NoAsapSnackbar.defaultProps = {
  open: false,
  onClose: () => {},
  snackbarHeadingOverride: ''
};

NoAsapSnackbar.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  pickupTime: PropTypes.string,
  pickupDate: PropTypes.string,
  deliveryDate: PropTypes.string,
  deliveryTime: PropTypes.string,
  isAsapAvailableForPickup: PropTypes.bool,
  isAsapAvailableForDelivery: PropTypes.bool,
  today: PropTypes.string,
  cartWorkingCopy: PropTypes.object,
  availableFulfillmentOptions: PropTypes.arrayOf(
    PropTypes.oneOf(ALL_ONLINE_ORDERING_FULFILLMENT_METHODS)
  ),
  snackbarHeadingOverride: PropTypes.string
};

export default NoAsapSnackbar;
